import React, { useRef, useState, useEffect } from 'react';
import { StyleSheet, ScrollView, Animated, View } from 'react-native';
import ConversationStarterItem from '@/components/ConversationStarterItem';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useUIStore } from '@/store';
import { MESSAGE_WIDTH } from '@/constants/config';

const DEFAULT_SUGGESTIONS = [
  'Tell me about yourself',
  'What can you help me with?',
  'What are your capabilities?',
  'How can we get started?'
];

const createStyles = (isSM, messageWidth) => {
  const customStyles = {
    conversationStarterItem: {}, 
  };

  if (!isSM && messageWidth === MESSAGE_WIDTH.NARROW) {
    customStyles.conversationStarterItem = {
      flex: 1,
      height: 72,
      width: '50%',
      minWidth: '48%',
      maxWidth: '50%',
    };
  } else if (!isSM && (messageWidth === MESSAGE_WIDTH.MEDIUM || messageWidth === MESSAGE_WIDTH.FULL)) {
    customStyles.conversationStarterItem = {
      flex: 1,
      height: 84,
      width: '25%',
      minWidth: '23%',
      maxWidth: '25%',
    };
  } else {
    customStyles.conversationStarterItem = {
      flex: 1,
      height: 86,
      width: '45%',
      minWidth: 220,
      maxWidth: 400,
    };
  }

  return StyleSheet.create({
    suggestionsContainer: {
      position: 'relative',
      left: 0,
      right: 0,
      paddingHorizontal: 0,
      flex: 1,
      width: '100%',
    },
    suggestionsScroll: {
      width: '100%',
      maxWidth: 'auto',
      marginHorizontal: 'auto',
      paddingLeft: isSM ? 10 : 0,
      paddingVertical: 10,
    },
    conversationStarterItems: {
      flexDirection: 'row',
      flexWrap: isSM ? 'nowrap' : 'wrap',
      gap: 12,
      width: '100%',
      justifyContent: 'flex-start',
      overflow: isSM ? 'auto' : 'visible',
    },
    conversationStarterItem: {
      ...customStyles.conversationStarterItem,
    },
  });
};

const ChatPageSuggestions = ({ 
  conversationStarters, 
  onSuggestionPress, 
  inputValue = '', 
  isMobile = false,
  animateOnMobile = false 
}) => {
  const { isSM } = useResponsiveness();
  const messageWidth = useUIStore(state => state.messageWidth);
  const styles = React.useMemo(() => createStyles(isSM, messageWidth), [isSM, messageWidth]);
  const suggestionsAnim = useRef(new Animated.Value(animateOnMobile ? 0 : 1)).current;
  const [suggestions, setSuggestions] = useState([]);

  // Initialize and shuffle suggestions
  useEffect(() => {
    if (conversationStarters?.length > 0) {
      const shuffled = [...conversationStarters];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setSuggestions(shuffled.slice(0, 4));
    } else {
      setSuggestions(DEFAULT_SUGGESTIONS);
    }
  }, [conversationStarters]);

  // Handle animation when input value changes (mobile only)
  React.useEffect(() => {
    if (!animateOnMobile) return;

    Animated.spring(suggestionsAnim, {
      toValue: inputValue.trim() ? 0 : 1,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start();
  }, [inputValue, animateOnMobile]);

  const animatedStyle = animateOnMobile ? {
    opacity: suggestionsAnim,
    transform: [{
      translateY: suggestionsAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [100, 0],
      })
    }],
    pointerEvents: inputValue.trim() ? 'none' : 'auto'
  } : {};

  return (
    <Animated.View style={[styles.suggestionsContainer, animatedStyle]}>
      <ScrollView
        horizontal={isSM}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.suggestionsScroll}
      >
        <View style={styles.conversationStarterItems}>
          {suggestions.map((suggestion, index) => (
            <ConversationStarterItem
              key={index}
              text={suggestion}
              style={styles.conversationStarterItem}
              onPress={onSuggestionPress}
              isHoverable={!isMobile}
            />
          ))}
        </View>
      </ScrollView>
    </Animated.View>
  );
};

export default React.memo(ChatPageSuggestions); 